import React, { useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend
);

type Props = {
  weeks: any;
  plannedWeekly: any;
  actualWeekly: any;
};

const WeeklyProgressDiffLineChart = ({ weeks, plannedWeekly, actualWeekly }: Props) => {
  const { t } = useTranslation();
  const dataList = useMemo(() => {
    return actualWeekly.map((o, i) => {
      if (o === null) {
        return null;
      } else {
        return o - (plannedWeekly[i] || 0);
      }
    });
  }, [plannedWeekly, actualWeekly]);

  const { data, options } = useMemo(() => {
    const d = {
      labels: weeks,
      datasets: [
        {
          type: 'line' as const,
          label: t('setup.label.전주(실적 - 계획)', '전주(실적 - 계획)') + '(%)',
          data: dataList,
          backgroundColor: 'rgba(73, 197, 94, 0.45)',
          borderColor: 'rgba(73, 197, 94, 1)',
          borderWidth: 1,
          yAxisID: 'y-left',
        },
      ],
    };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            generateLabels: (chart: any) => {
              const defaults = ChartJS.defaults.plugins.legend.labels.generateLabels(chart) ?? [];
              return defaults.map((legendItem) => {
                const svgType = chart?.data?.datasets?.[legendItem.datasetIndex || 0]?.type;
                const svgColor = chart?.data?.datasets?.[legendItem.datasetIndex || 0]?.borderColor;
                if (svgType === 'line') {
                  const icon = getLegendIcon(svgColor);
                  if (icon) {
                    legendItem.pointStyle = icon;
                  }
                } else {
                  legendItem.pointStyle = 'rect';
                }
                return legendItem;
              });
            },
          },
        },
        tooltip: { enabled: true },
      },
      scales: {
        x: {
          display: true,
          ticks: { maxRotation: 0, autoSkip: false },
        },
        'y-left': {
          type: 'linear' as const,
          position: 'left' as const,
          suggestedMin: -10,
          suggestedMax: 10,
          title: {
            display: true,
            text: t('setup.label.전주(실적 - 계획)', '전주(실적 - 계획)') + '(%)',
          },
        },
      },
    };

    return {
      data: d,
      options: opts,
      dataList,
    };
  }, [weeks, dataList]);

  const tableRows = useMemo(() => {
    return [
      {
        key: 'dataList',
        label: t('setup.label.전주(실적 - 계획)', '전주(실적 - 계획)') + '(%)',
        color: 'rgba(73, 197, 94, 0.85)',
        shape: 'line',
        values: dataList,
      },
    ];
  }, [dataList, weeks]);

  const chartRef = useRef<InstanceType<typeof ChartJS> | null>(null);
  const [tableMargins, setTableMargins] = useState({ left: 0, right: 0, cellWidth: 50 });

  const getLegendIcon = (color: string) => {
    const img = new Image();
    const svgDataUrl = (svg: string) =>
      `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    img.src = svgDataUrl(
      `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 28 12"><polyline points="0,10 8,2 16,6 24,0 28,4" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    );
    return img;
  };

  const marginPlugin = useMemo(
    () => ({
      id: 'table-margin-plugin',
      afterLayout: (chart: any) => {
        try {
          const left = chart.scales?.['y-left']?.width ?? 0;
          const right = chart.scales?.['y-right']?.width ?? 0;
          const chartInner = chart.chartArea?.width ?? 0;
          const cellWidth =
            weeks?.length > 0 ? Math.max(32, Number((chartInner / weeks.length).toFixed(2))) : 50;
          setTableMargins((prev) =>
            prev.left === left && prev.right === right && prev.cellWidth === cellWidth
              ? prev
              : { left, right, cellWidth }
          );
        } catch (e) {
          // swallow errors silently; measurement is best-effort
        }
      },
    }),
    []
  );

  return (
    <div style={{ width: '100%', padding: 12 }}>
      <div style={{ height: 420 }}>
        <Chart ref={chartRef} type="bar" data={data} options={options} plugins={[marginPlugin]} />
      </div>

      {/* Data table aligned to weeks */}
      <div style={{ marginTop: 12, marginRight: tableMargins.right }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {tableRows.map((row) => (
              <tr key={row.key}>
                <td
                  style={{
                    verticalAlign: 'middle',
                    border: '1px solid #e5e7eb',
                    width: tableMargins.left,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                    }}
                  >
                    {/* legend glyph */}
                    {row.shape === 'bar' ? (
                      <span
                        style={{
                          width: 24,
                          height: 12,
                          background: row.color,
                          display: 'inline-block',
                          borderRadius: 3,
                          border: '1px solid rgba(0,0,0,0.08)',
                        }}
                      />
                    ) : (
                      <svg
                        width="28"
                        height="12"
                        viewBox="0 0 28 12"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <polyline
                          points="0,10 8,2 16,6 24,0 28,4"
                          fill="none"
                          stroke={row.color}
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {/* <div style={{ fontSize: 13 }}>{row.label}</div> */}
                  </div>
                </td>
                {row.values.map((value, i) => (
                  <td
                    key={i}
                    style={{
                      textAlign: 'center',
                      border: '1px solid #e5e7eb',
                      fontSize: 13,
                      width: tableMargins.cellWidth,
                      fontWeight: i === 6 ? 'bold' : '',
                      backgroundColor: i === 6 ? 'rgba(255, 255, 0, 0.85)' : '',
                    }}
                  >
                    {value !== null ? `${value.toFixed(1)}%` : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeeklyProgressDiffLineChart;
